import { type FC, useCallback, useState } from 'react';
import { GenerateConsole } from '../../components/GenerateConsole';
import { NoSpecSelected } from '../../components/NoSpecSelected';
import {
  StrategySelector,
  StrategySelectorControls,
  StrategySelectorHeader,
  StrategySelectorList,
  StrategySelectorTitle,
} from '../../components/StrategySelector';
import { useSpec } from '../../contexts/SpecContext';
import { useGenerationSocket } from '../../hooks/useGenerationSocket';
import { usePassiveScansData } from './hooks/usePassiveScansData';
import { ScanResults } from './ScanResults';
import type { PassiveScanResults } from './types';
import styles from './PassiveScans.module.css';

const API_URL = import.meta.env.VITE_API_URL || '';

const SCAN_STATUS_LABELS = {
  running: 'Scan in progress...',
  success: 'Scan completed successfully',
  error: 'Scan failed',
  stopped: 'Scan stopped by user',
} as const;

export const PassiveScans: FC = () => {
  const { hasActiveSpec, activeSpec } = useSpec();
  const {
    strategies: passiveStrategies,
    selectedStrategies,
    selectedSet,
    loading,
    toggleStrategy,
    selectAll,
    deselectAll,
  } = usePassiveScansData();

  const [scanResults, setScanResults] = useState<PassiveScanResults | null>(null);

  const handleScanComplete = useCallback((data: Record<string, unknown>) => {
    if (data.results) {
      setScanResults(data.results as PassiveScanResults);
    }
  }, []);

  const { generating, consoleOutput, generationStatus, startGeneration, failGeneration, stopGeneration } =
    useGenerationSocket({
      eventPrefix: 'passive-scan',
      schemaId: activeSpec?.id,
      onComplete: handleScanComplete,
    });

  const handleRunPassiveScans = useCallback(async () => {
    if (!activeSpec || selectedStrategies.length === 0) return;

    setScanResults(null);
    startGeneration('Starting passive scans...\n');

    try {
      const response = await fetch(`${API_URL}/api/generate/passive-scans/${activeSpec.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passiveStrategies: selectedStrategies }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        failGeneration(`Error: ${data.message || 'Failed to start passive scans'}`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error starting passive scans:', error);
      failGeneration(`Error: ${message}`);
    }
  }, [activeSpec, selectedStrategies, startGeneration, failGeneration]);

  const handleStopScans = useCallback(async () => {
    if (!activeSpec) return;

    try {
      await fetch(`${API_URL}/api/generate/passive-scans/${activeSpec.id}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      stopGeneration();
    } catch (error) {
      console.error('Error stopping passive scans:', error);
    }
  }, [activeSpec, stopGeneration]);

  if (!hasActiveSpec) {
    return <NoSpecSelected />;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <h3>Loading passive scans...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Passive Scans</h1>
        <p className={styles.description}>
          Passive scans analyze API responses for security issues without modifying requests. These checks look for
          exposed headers, leaked secrets, data exposure patterns, and more.
        </p>
      </div>

      <StrategySelector strategies={passiveStrategies} selectedKeys={selectedSet} onToggle={toggleStrategy}>
        <StrategySelectorHeader>
          <StrategySelectorTitle>Passive Strategies</StrategySelectorTitle>
          <StrategySelectorControls onSelectAll={selectAll} onDeselectAll={deselectAll} />
        </StrategySelectorHeader>
        <StrategySelectorList emptyMessage="No passive strategies available." />
      </StrategySelector>

      <GenerateConsole
        generating={generating}
        consoleOutput={consoleOutput}
        generationStatus={generationStatus}
        disabled={!activeSpec || selectedStrategies.length === 0}
        title="Run Scans"
        runLabel="Run Passive Scans"
        stopLabel="Stop Scans"
        statusLabels={SCAN_STATUS_LABELS}
        onGenerate={handleRunPassiveScans}
        onStop={handleStopScans}
      />

      {scanResults && <ScanResults results={scanResults} />}
    </div>
  );
};
